import { useMemo } from "react";

import {
  Badge,
  FontIcon,
  TableColumnDefinition,
  TableRowDefinition,
  TableWithDefaultFilters,
} from "@ezmid/components-library";
import {
  faDatabase,
  faFingerprint,
  faMobile,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";

import { useGetUserDevicesByUserIdQuery } from "../../../api/user-devices.generated";
import { useFormatters } from "../../../hooks";

type TableData = {
  deviceType: string;
  notificationToken: string;
  count: number;
  createdAt: string;
};

type Props = {
  id: string;
};

export function UserDevicesCard<T extends object>({ id }: Props) {
  const { t } = useTranslation();
  const { formatDate } = useFormatters();
  const { currentData, isFetching } = useGetUserDevicesByUserIdQuery(
    {
      id: id ?? "",
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const columns: Array<TableColumnDefinition<TableData>> = useMemo(
    () => [
      {
        Header: t("table.header.deviceType"),
        id: "deviceType",
        accessor: "deviceType",
      },
      {
        Header: t("table.header.notificationToken"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Badge
            variant="pill"
            leftIcon={
              <FontIcon
                marginTop={4}
                customIcon={<FontAwesomeIcon size="lg" icon={faFingerprint} />}
              />
            }
          >
            {row?.original.notificationToken.toUpperCase().substr(0, 8)}
          </Badge>
        ),
        id: "notificationToken",
        accessor: "notificationToken",
      },
      {
        Header: t("table.header.createdAt"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <span>{formatDate(row?.original.createdAt)}</span>
        ),
        id: "createdAt",
        accessor: "createdAt",
      },
      {
        Header: t("table.header.count"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Badge
            variant="pill"
            status="break"
            leftIcon={
              <FontIcon
                marginTop={3}
                customIcon={<FontAwesomeIcon size="lg" icon={faDatabase} />}
              />
            }
          >
            {row?.original.count ?? ""}
          </Badge>
        ),
        id: "count",
        accessor: "count",
      },
    ],
    []
  );

  const memoizedData: Array<TableData> = useMemo(
    () =>
      currentData?.items?.map((item) => {
        return {
          deviceType: item.type || "",
          notificationToken: item?.notificationToken || "",
          count: currentData?.count || 0,
          createdAt: item?.createdAt || "",
        };
      }) || [],
    [currentData]
  );

  return (
    <TableWithDefaultFilters
      tableData={memoizedData}
      tableColumns={columns}
      showPagination={false}
      showToolbar={false}
      headerIcon={
        <FontIcon
          width={16}
          height={16}
          customIcon={<FontAwesomeIcon size="lg" icon={faMobile} />}
        />
      }
      headerText={t("user.devices.information")}
      isLoading={isFetching}
    />
  );
}
